import * as ArchiveActions from 'archive/ArchiveActions';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ACTION_ARCHIVE_ROWS} from 'notebook/store/VersioningConstants';
import makeArchiveRowActionFunction from 'notebook/structured-tables/action-functions/makeArchiveRowActionFunction';
import {
  ActionResultType,
  performServerSideTableWriteActionWithAudits,
} from 'notebook/structured-tables/action-functions/PerformActionFunctions';
import {archiveRows, unarchiveRows} from 'notebook/structured-tables/ActionFunctionDAO';
import {
  AxisContextMenuActionFunctionParameters,
  StructuredTableRow,
} from 'notebook/structured-tables/StructuredTableConstants';
import ArchiveOutputsModal from 'processes/modals/ArchiveOutputsModal';
import runModal from 'ui-library/runModal';

import ProcessOutputStructuredTableAdapter, {
  ProcessOutputStructuredTable,
  ProcessOutputStructuredTableMetadata,
  ProcessOutputStructuredTableRowMetadata,
} from '../ProcessOutputStructuredTableAdapter';

function updateOutputTable(
  actionFunctionParams: AxisContextMenuActionFunctionParameters<
    ProcessOutputStructuredTableMetadata,
    ProcessOutputStructuredTableRowMetadata
  >,
  itemIds: Array<string>,
  newArchivePurpose: string | null
): void {
  const adapter = actionFunctionParams.structuredTableAdapter as ProcessOutputStructuredTableAdapter;
  const lastSyncedArchivePurposeByRow = actionFunctionParams.structuredTable.rows.map((row) => {
    const outputIdForRow = adapter.getOutputId(actionFunctionParams.structuredTable.columns, row);
    return outputIdForRow && itemIds.includes(outputIdForRow) ? newArchivePurpose : undefined;
  });
  const updatedCells = actionFunctionParams.structuredTable.rows.map((row) => row.cells);
  actionFunctionParams.updateHandler({
    updatedCells,
    columnUpdates: {},
    lastSyncedArchivePurposeByRow,
  });
}

export const ARCHIVE_OUTPUT_ROW_ACTION_FUNCTION_CONFIG = {
  allowOutsideOfEntry: true,

  getSelectedItems: (
    structuredTable: ProcessOutputStructuredTable,
    selectedRows: Array<StructuredTableRow<ProcessOutputStructuredTableRowMetadata>>,
    adapter: ProcessOutputStructuredTableAdapter
  ): Array<{id: string; rowId: string; modelType: ModelTypeConstants}> => {
    const outputItems: Array<{id: string; rowId: string; modelType: ModelTypeConstants}> = [];
    for (const row of selectedRows) {
      const outputId = adapter.getOutputId(structuredTable.columns, row);
      if (outputId) {
        outputItems.push({id: outputId, rowId: row.rowId, modelType: ModelTypeConstants.PROCESS_OUTPUT});
      }
    }
    return outputItems;
  },
  onSelectForArchive: async (
    actionFunctionParams: AxisContextMenuActionFunctionParameters<
      ProcessOutputStructuredTableMetadata,
      ProcessOutputStructuredTableRowMetadata
    >,
    items: Array<{id: string; rowId: string; modelType: ModelTypeConstants}>
  ): Promise<void> => {
    const itemIdToRowId: {[id: string]: string} = {};
    for (const item of items) {
      itemIdToRowId[item.id] = item.rowId;
    }
    const archiveData = await runModal(ArchiveOutputsModal, {
      outputIds: items.map(({id}) => id),
    });
    if (archiveData != null) {
      if (actionFunctionParams.entryOrTemplateId == null) {
        await ArchiveActions.bulkArchiveProcessManagement(
          ModelTypeConstants.PROCESS_OUTPUT,
          archiveData.itemIds,
          archiveData.archivePurpose
        );
        updateOutputTable(actionFunctionParams, archiveData.itemIds, archiveData.archivePurpose);
      } else {
        performServerSideTableWriteActionWithAudits({
          entryId: actionFunctionParams.entryOrTemplateId,
          loadingScreenMessage: 'Archiving rows...',
          autoSaverAction: ACTION_ARCHIVE_ROWS,
          doPerformAction: async ({dataModsHeader}) => {
            await archiveRows(
              actionFunctionParams.entryOrTemplateId!,
              actionFunctionParams.structuredTable,
              actionFunctionParams.computedTable,
              actionFunctionParams.actionFunctionContext,
              archiveData.itemIds.map((id) => ({
                id,
                rowId: itemIdToRowId[id],
                modelType: ModelTypeConstants.PROCESS_OUTPUT,
              })),
              archiveData.archivePurpose,
              undefined,
              dataModsHeader
            );
            return {resultType: ActionResultType.SUCCESS, responseData: null};
          },
          analyticsActionForPerfTracking: 'archive_process_output_structured_table_rows',
          structuredTables: [actionFunctionParams.structuredTable],
          structuredTableAdapters: [actionFunctionParams.structuredTableAdapter],
          computedTables: [actionFunctionParams.computedTable],
          tables: actionFunctionParams.table ? [actionFunctionParams.table] : [],
        });
      }
    }
  },
  onSelectForUnarchive: async (
    actionFunctionParams: AxisContextMenuActionFunctionParameters<
      ProcessOutputStructuredTableMetadata,
      ProcessOutputStructuredTableRowMetadata
    >,
    items: Array<{id: string; modelType: ModelTypeConstants}>
  ): Promise<void> => {
    if (actionFunctionParams.entryOrTemplateId == null) {
      const itemIdsForUnarchive = items.map(({id}) => id);
      await ArchiveActions.bulkUnarchiveProcessManagement(
        ModelTypeConstants.PROCESS_OUTPUT,
        itemIdsForUnarchive
      );
      updateOutputTable(actionFunctionParams, itemIdsForUnarchive, null);
    } else {
      performServerSideTableWriteActionWithAudits({
        entryId: actionFunctionParams.entryOrTemplateId,
        loadingScreenMessage: 'Unarchiving rows...',
        autoSaverAction: ACTION_ARCHIVE_ROWS,
        doPerformAction: async ({dataModsHeader}) => {
          await unarchiveRows(
            actionFunctionParams.entryOrTemplateId!,
            actionFunctionParams.structuredTable,
            actionFunctionParams.computedTable,
            actionFunctionParams.actionFunctionContext,
            items,
            dataModsHeader
          );
          return {resultType: ActionResultType.SUCCESS, responseData: null};
        },
        analyticsActionForPerfTracking: 'unarchive_process_output_structured_table_rows',
        structuredTables: [actionFunctionParams.structuredTable],
        structuredTableAdapters: [actionFunctionParams.structuredTableAdapter],
        computedTables: [actionFunctionParams.computedTable],
        tables: actionFunctionParams.table ? [actionFunctionParams.table] : [],
      });
    }
  },
};

export default makeArchiveRowActionFunction<
  ProcessOutputStructuredTableMetadata,
  ProcessOutputStructuredTableRowMetadata
>(ARCHIVE_OUTPUT_ROW_ACTION_FUNCTION_CONFIG);
